import React from "react";
import Balance from "../../../components/numbers/balance";
import { InvestmentV2 } from "../../../models/investment-v2";
import { selectedInvestmentsByMoneyTypeSelector } from "../../../selectors/investments";
import { useSelector } from "../../../store/store";
import { calculateTotalContributionsThisYear } from "./manage-contributions-utilities";
import { PayCheckFrequency } from "./paycheck-frequency";

type Props = {
  totalModifiedAmount: number;
  totalContributionAmount: number;
  allFields: any;
};

function ContributionLimits(props: Props) {
  const { totalModifiedAmount, totalContributionAmount, allFields } = props;

  const { contributionLimit, contributionEffectiveDate } = useSelector(
    (state) => state.plan.info
  );

  const displayYear = new Date(
    contributionEffectiveDate ?? Date.now()
  ).getFullYear();

  const investmentsByMoneyType: InvestmentV2[] = useSelector(
    selectedInvestmentsByMoneyTypeSelector
  );
  const contributionsYTD = calculateTotalContributionsThisYear(
    investmentsByMoneyType
  );

  const payCheckFrequency: PayCheckFrequency = allFields.payCheckFrequency;
  const bonusPayCheckFrequency: PayCheckFrequency =
    allFields.bonusPayCheckFrequency;

  const contributionAmountLeft: number =
    (contributionLimit ?? 0) - contributionsYTD;
  const remainingWeeks: number = remainingWeeksFromDate(
    new Date(contributionEffectiveDate ?? Date.now())
  );

  const totalAmount: number =
    totalModifiedAmount *
      actualRemainingWeeksForPayCheck(payCheckFrequency, remainingWeeks) +
    totalContributionAmount *
      actualRemainingWeeksForBonus(bonusPayCheckFrequency, remainingWeeks);

  const contributionChangesTotal: number =
    (contributionLimit ?? 0) - (contributionsYTD + totalAmount);

  return (
    <>
      <div className="manage-contributions-text">
        <p data-testid="contributionsYTD">
          You've contributed{" "}
          <span className="bold">
            <Balance showDollar value={contributionsYTD} />
          </span>{" "}
          so far this year.
        </p>
        <p data-testid="contributionAmountLeft">
          That means that you have{" "}
          <span className="bold">
            <Balance showDollar value={contributionAmountLeft} />
          </span>{" "}
          until you hit the {displayYear} maximum contribution limit of{" "}
          <span className="bold">
            <Balance showDollar value={contributionLimit ?? 0} />
          </span>{" "}
          .
        </p>
        {updatedContributionChanges(contributionChangesTotal)}
      </div>
    </>
  );
}

function actualRemainingWeeksForPayCheck(
  payCheckFrequency: PayCheckFrequency,
  remainingWeeks: number
): number {
  switch (payCheckFrequency) {
    case PayCheckFrequency.WEEKLY:
      return remainingWeeks;
    case PayCheckFrequency.BIWEEKLY:
      return remainingWeeks / 2;
    case PayCheckFrequency.MONTHLY:
      return remainingWeeks / 4;
    case PayCheckFrequency.BIMONTHLY:
      return remainingWeeks / 8;
    default:
      return 0;
  }
}

function actualRemainingWeeksForBonus(
  bonusPayCheckFrequency: PayCheckFrequency,
  remainingWeeks: number
): number {
  switch (bonusPayCheckFrequency) {
    case PayCheckFrequency.ANNUALLY:
      return remainingWeeks / 52;
    case PayCheckFrequency.QUARTERLY:
      return remainingWeeks / 13;
    case PayCheckFrequency.MONTHLY:
      return remainingWeeks / 4;
    default:
      return 0;
  }
}

function remainingWeeksFromDate(date: Date) {
  const year = date.getFullYear();
  const start = new Date(year, date.getMonth(), date.getDate());
  const end = new Date(year, 11, 31);
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / msPerWeek));
}

function updatedContributionChanges(contributionChangesTotal: number) {
  if (contributionChangesTotal > 0) {
    return (
      <>
        Based upon your updated contribution changes, you are projected to be{" "}
        <span className="bold">under</span> the maximum contribution limit by{" "}
        <span className="bold">
          <Balance value={contributionChangesTotal} showDollar />.
        </span>
      </>
    );
  }
  return (
    <>
      Based upon your updated contribution changes, you are projected to be{" "}
      <span className="bold italic">over</span> the maximum contribution limit
      by{" "}
      <span className="bold italic">
        <Balance value={Math.abs(contributionChangesTotal)} showDollar />.
      </span>
    </>
  );
}

export default ContributionLimits;
